import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { Container } from '../../styles/components.js';
import { projects } from '../../constants/data.js';
import { FaApple, FaGooglePlay } from 'react-icons/fa';

/* ─── Section ────────────────────────────────────────────────── */
const ProjectsSection = styled.section`
  padding: ${({ theme }) => theme.spacing.xxl} 0;
  background-color: ${({ theme }) => theme.colors.surface};
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: ${({ theme }) => theme.colors.border};
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 650px;
  margin: 0 auto ${({ theme }) => theme.spacing.xl};
`;

const Label = styled.div`
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  background: ${({ theme }) => theme.colors.primary}0a;
  padding: 5px 14px;
  border-radius: ${({ theme }) => theme.borderRadius.full};
  border: 1px solid ${({ theme }) => theme.colors.primary}18;
  margin-bottom: 1rem;
`;

const Title = styled.h2`
  font-size: clamp(1.8rem, 3.5vw, 2.6rem);
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 1rem;
  letter-spacing: -0.03em;

  span {
    background: ${({ theme }) => theme.colors.gradient};
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Desc = styled.p`
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.textGray};
  line-height: 1.7;
  margin-bottom: 0;
`;

/* ─── Grid ───────────────────────────────────────────────────── */
const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
  }
`;

/* ─── Card ───────────────────────────────────────────────────── */
const ProjectCard = styled(motion.div)`
  background: ${({ theme }) => theme.colors.background};
  border-radius: 24px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.card};
  display: flex;
  flex-direction: column;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);

  &:hover {
    box-shadow: 0 30px 60px rgba(0, 0, 0, 0.08);
    border-color: ${({ $themeColor }) => $themeColor ? `${$themeColor}40` : 'transparent'};
    transform: translateY(-6px);
  }
`;

const CardHeader = styled.div`
  height: 170px;
  background: ${({ $color, theme }) =>
    `linear-gradient(135deg, ${$color || theme.colors.primary}0a 0%, ${$color || theme.colors.primary}18 100%)`};
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.75rem;
  position: relative;
  overflow: hidden;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &::before {
    content: '';
    position: absolute;
    bottom: -30px;
    right: -30px;
    width: 130px;
    height: 130px;
    border-radius: 50%;
    background: ${({ $color, theme }) => ($color || theme.colors.primary)}10;
    filter: blur(10px);
  }
`;

const LogoWrapper = styled.div`
  width: 68px;
  height: 68px;
  border-radius: 18px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.small};
  transition: all 0.3s cubic-bezier(0.16, 1, 0.3, 1);
  flex-shrink: 0;

  ${ProjectCard}:hover & {
    transform: scale(1.08) rotate(-4deg);
    border-color: ${({ $themeColor }) => $themeColor ? `${$themeColor}40` : 'transparent'};
    box-shadow: ${({ theme }) => theme.shadows.glow};
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .emoji {
    font-size: 2rem;
  }
`;

const StoreBadges = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  position: relative;
  z-index: 1;
`;

const StoreBadge = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 10px;
  font-size: 0.7rem;
  font-weight: 700;
  text-decoration: none;
  background: ${({ theme }) => theme.colors.surface};
  color: ${({ theme }) => theme.colors.textDark};
  border: 1px solid ${({ theme }) => theme.colors.border};
  transition: all 0.25s ease;
  white-space: nowrap;

  &:hover {
    border-color: ${({ $themeColor, theme }) => $themeColor || theme.colors.primary};
    color: ${({ $themeColor, theme }) => $themeColor || theme.colors.primary};
    background: ${({ $themeColor, theme }) => ($themeColor ? `${$themeColor}0a` : `${theme.colors.primary}0a`)};
  }

  svg {
    width: 13px;
    height: 13px;
  }
`;

const CardBody = styled.div`
  padding: 1.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProjectTitle = styled.h3`
  font-size: 1.15rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 0.6rem;
  font-family: 'Space Grotesk', sans-serif;
  letter-spacing: -0.02em;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 1.25rem;
`;

const Tag = styled.span`
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 700;
  background: ${({ $color, theme }) => `${$color || theme.colors.primary}0c`};
  color: ${({ $color, theme }) => $color || theme.colors.primary};
  border: 1px solid ${({ $color, theme }) => `${$color || theme.colors.primary}18`};
`;

const ProjectDesc = styled.p`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.textGray};
  line-height: 1.65;
  flex: 1;
  margin-bottom: 1.5rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CaseStudyBtn = styled.button`
  width: 100%;
  padding: 0.8rem;
  border-radius: 99px;
  font-size: 0.85rem;
  font-weight: 700;
  font-family: 'Space Grotesk', sans-serif;
  background: transparent;
  color: ${({ $color, theme }) => $color || theme.colors.primary};
  border: 1.5px solid ${({ $color, theme }) => $color || theme.colors.primary};
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 0.01em;

  ${ProjectCard}:hover & {
    background: ${({ $color, theme }) => $color || theme.colors.primary};
    color: #ffffff;
    box-shadow: 0 8px 20px ${({ $color, theme }) => $color ? `${$color}20` : 'transparent'};
  }
`;

/* ─── Modal ─────────────────────────────────────────────────── */
const Backdrop = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(3, 3, 3, 0.8);
  backdrop-filter: blur(12px);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
`;

const ModalBox = styled(motion.div)`
  background: ${({ theme }) => theme.colors.surface};
  border-radius: 28px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  box-shadow: ${({ theme }) => theme.shadows.large};
  width: 100%;
  max-width: 650px;
  max-height: 85vh;
  overflow-y: auto;
  position: relative;

  &::-webkit-scrollbar { width: 5px; }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.border};
    border-radius: 99px;
  }
`;

const ModalHeader = styled.div`
  height: 180px;
  background: ${({ $color, theme }) =>
    `linear-gradient(135deg, ${$color || theme.colors.primary}0a 0%, ${$color || theme.colors.primary}18 100%)`};
  display: flex;
  align-items: flex-end;
  padding: 2rem;
  position: relative;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const ModalLogo = styled.div`
  width: 76px;
  height: 76px;
  border-radius: 18px;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${({ theme }) => theme.shadows.large};
  overflow: hidden;
  margin-right: 1.25rem;
  flex-shrink: 0;

  img { width: 100%; height: 100%; object-fit: cover; }
  .emoji { font-size: 2.2rem; }
`;

const ModalTitleArea = styled.div``;

const ModalTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.textDark};
  margin-bottom: 0.25rem;
  letter-spacing: -0.02em;
`;

const ModalCategory = styled.p`
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textGray};
  letter-spacing: 0.05em;
  margin: 0;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 1.25rem;
  right: 1.25rem;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.surface};
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 1rem;
  color: ${({ theme }) => theme.colors.textDark};
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  z-index: 2;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary}40;
    color: ${({ theme }) => theme.colors.primary};
    background: ${({ theme }) => theme.colors.primary}0a;
  }
`;

const ModalBody = styled.div`
  padding: 2.25rem;
`;

const ModalSection = styled.div`
  margin-bottom: 1.75rem;
`;

const ModalSectionTitle = styled.h4`
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 0.75rem;
`;

const ModalDesc = styled.p`
  font-size: 0.95rem;
  line-height: 1.75;
  color: ${({ theme }) => theme.colors.textGray};
  margin-bottom: 0;
  white-space: pre-line;
`;

const TagsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const ModalLinks = styled.div`
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 2rem;
  padding-top: 1.75rem;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`;

const StoreLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0.75rem 1.4rem;
  border-radius: 99px;
  font-size: 0.85rem;
  font-weight: 700;
  text-decoration: none;
  transition: all 0.25s cubic-bezier(0.16, 1, 0.3, 1);
  font-family: 'Space Grotesk', sans-serif;
  box-shadow: ${({ theme }) => theme.shadows.glow};

  &.ios {
    background: #000;
    color: #fff;
    &:hover { background: #222; transform: translateY(-2px); box-shadow: 0 10px 20px rgba(0,0,0,0.15); }
  }
  &.android {
    background: #01875f;
    color: #fff;
    &:hover { background: #016e4f; transform: translateY(-2px); box-shadow: 0 10px 20px rgba(1,135,95,0.15); }
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

/* ─── Component ─────────────────────────────────────────────── */
const Projects = () => {
  const [selected, setSelected] = useState(null);

  return (
    <ProjectsSection id="projects">
      <Container>
        <SectionHeader>
          <Label>03 / MOBILE PROJECTS</Label>
          <Title>React Native <span>Projects</span></Title>
          <Desc>
            Explore key projects built with React Native that showcase my expertise in mobile app development.
          </Desc>
        </SectionHeader>

        <ProjectGrid>
          {projects.map((project, index) => (
            <ProjectCard
              key={project.id}
              $themeColor={project.color}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              onClick={() => setSelected(project)}
            >
              <CardHeader $color={project.color}>
                <LogoWrapper $themeColor={project.color}>
                  {project.logo
                    ? <img src={project.logo} alt={project.title} />
                    : <span className="emoji">{project.icon}</span>
                  }
                </LogoWrapper>
                <StoreBadges onClick={e => e.stopPropagation()}>
                  {project.link && (
                    <StoreBadge href={project.link} target="_blank" rel="noopener noreferrer" $themeColor={project.color}>
                      <FaApple /> App Store
                    </StoreBadge>
                  )}
                  {project.androidLink && (
                    <StoreBadge href={project.androidLink} target="_blank" rel="noopener noreferrer" $themeColor={project.color}>
                      <FaGooglePlay /> Play Store
                    </StoreBadge>
                  )}
                </StoreBadges>
              </CardHeader>

              <CardBody>
                <ProjectTitle>{project.title}</ProjectTitle>
                <TagRow>
                  {project.tags.slice(0, 3).map((tag, i) => (
                    <Tag key={i} $color={project.color}>{tag}</Tag>
                  ))}
                  {project.tags.length > 3 && (
                    <Tag $color={project.color}>+{project.tags.length - 3}</Tag>
                  )}
                </TagRow>
                <ProjectDesc>
                  {project.description?.length > 130
                    ? `${project.description.slice(0, 130)}...`
                    : project.description}
                </ProjectDesc>
                <CaseStudyBtn onClick={() => setSelected(project)} $color={project.color}>
                  View Case Study
                </CaseStudyBtn>
              </CardBody>
            </ProjectCard>
          ))}
        </ProjectGrid>
      </Container>

      {/* ─── Modal ─── */}
      <AnimatePresence>
        {selected && (
          <Backdrop
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelected(null)}
          >
            <ModalBox
              initial={{ opacity: 0, y: 40, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.97 }}
              transition={{ type: 'spring', damping: 28, stiffness: 280 }}
              onClick={e => e.stopPropagation()}
            >
              <ModalHeader $color={selected.color}>
                <ModalLogo>
                  {selected.logo
                    ? <img src={selected.logo} alt={selected.title} />
                    : <span className="emoji">{selected.icon}</span>
                  }
                </ModalLogo>
                <ModalTitleArea>
                  <ModalTitle>{selected.title}</ModalTitle>
                  <ModalCategory>React Native Mobile App</ModalCategory>
                </ModalTitleArea>
                <CloseBtn onClick={() => setSelected(null)}>✕</CloseBtn>
              </ModalHeader>

              <ModalBody>
                <ModalSection>
                  <ModalSectionTitle>📋 About This Project</ModalSectionTitle>
                  <ModalDesc>{selected.description?.trim()}</ModalDesc>
                </ModalSection>

                <ModalSection>
                  <ModalSectionTitle>🛠 Tech Stack</ModalSectionTitle>
                  <TagsWrap>
                    {selected.tags.map((tag, i) => (
                      <Tag key={i} $color={selected.color}>{tag}</Tag>
                    ))}
                  </TagsWrap>
                </ModalSection>

                {(selected.link || selected.androidLink) && (
                  <ModalLinks>
                    {selected.link && (
                      <StoreLink
                        href={selected.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ios"
                      >
                        <FaApple /> Download on App Store
                      </StoreLink>
                    )}
                    {selected.androidLink && (
                      <StoreLink
                        href={selected.androidLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="android"
                      >
                        <FaGooglePlay /> Get on Google Play
                      </StoreLink>
                    )}
                  </ModalLinks>
                )}
              </ModalBody>
            </ModalBox>
          </Backdrop>
        )}
      </AnimatePresence>
    </ProjectsSection>
  );
};

export default Projects;